<template>
  <div>
    <md-card
      class="md-accent invalid-form-message"
      v-if="fieldsErrors.places">
      <md-ripple>
        <md-card-header>
          <div class="md-title">Le formulaire est invalide</div>
        </md-card-header>

        <md-card-content>
          {{ fieldsErrors.places }}
        </md-card-content>
      </md-ripple>
    </md-card>
    <md-table
      v-model="paginatedData"
      :md-selected-value="selectedPlaces"
      @md-selected="onSelect"
      md-card
      :md-sort="sortInput.name"
      :md-sort-type="sortInput.type"
      :class="{ 'dialog-places-table': true, 'dialog-tables-margin': true, 'md-invalid': fieldsErrors.places }">
      <md-table-toolbar>
        <h1 class="md-title table-title">Lieux</h1>

        <div class="md-layout md-gutter md-alignment-center search-fields">
          <div class="md-layout-item">
            <md-field
              md-clearable
              class="md-toolbar-section-end">
              <md-input
                placeholder="Recherche par nom..."
                v-model="searchTerm"
                @input="searchOnTable" />
            </md-field>
          </div>
          <div class="md-layout-item">
            <md-field :class="{ 'md-has-value': true, 'md-required': true }">
              <multiselect
                v-model="searchTags"
                :options="tagsData"
                :multiple="true"
                :limit="1"
                :limit-text="multiselectLimitText"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="tagChoiceName"
                @select="searchOnTable"
                @remove="searchOnTable"
                label="name"
                track-by="id"
                placeholder="Recherche par tag..."
                select-label="Sélectionner"
                select-group-label="Sélectionner le groupe"
                selected-label="Sélectionné ✓"
                deselect-label="Désélectionner ✗"
                deselect-group-label="Désélectionner le groupe ✗"
                class="multiselect-transparent">
                <span slot="noResult">Aucun tag trouvé, veuillez modifier votre recherche.</span>
              </multiselect>
            </md-field>
          </div>
        </div>
        <div
          v-if="showCriterionAlert"
          class="criterion-alert">
          Prenez explicitement note de vos critères de choix pour vous en rappeler !
        </div>
      </md-table-toolbar>
      <md-table-toolbar
        slot="md-table-alternate-header"
        slot-scope="{ count }">
        <div class="md-toolbar-section-start">{{ getSelectedCountLabel(count) }}</div>

        <div
          class="md-toolbar-section-end"
          v-if="editable">
          <md-button
            class="md-icon-button"
            @click="emptySelection">
            <md-icon>delete</md-icon>
            <md-tooltip md-direction="left">Tout déselectionner</md-tooltip>
          </md-button>
        </div>
      </md-table-toolbar>

      <md-table-empty-state
        md-label="Aucun lieux trouvés"
        :md-description="getEmptyDescription()" />

      <md-table-row
        slot="md-table-row"
        slot-scope="{ item }"
        md-selectable="multiple"
        :md-disabled="isDisabled(item)">
        <md-table-cell
          md-label="Lieu"
          md-sort-by="name">
          <strong>{{ item.name }}</strong>
          <md-icon v-if="isDisabled(item)">
            not_interested
            <md-tooltip
              md-direction="top">Un autre paramètre est déjà attribué à ce lieu</md-tooltip>
          </md-icon>
          <br>
          <em>{{ item.ancestors_name }}</em>
        </md-table-cell>
        <md-table-cell
          md-label="Identifiant"
          md-sort-by="identifier">{{ item.identifier }}</md-table-cell>
        <md-table-cell
          md-label="Fonction"
          md-sort-by="function">{{ item.function }}</md-table-cell>
        <md-table-cell
          md-label="Tags"
          md-sort-by="tags">
          <TagsListWidget
            :items="item.tags"
            :visible-items-count="3" />
        </md-table-cell>
      </md-table-row>
    </md-table>
    <paginator
      :md-page-size="10"
      :md-data="searched"
      :md-page-options="[10, 50, 100, 1000, 10000]"
      :md-sort="sortInput.name"
      :md-sort-order="sortInput.type"
      :md-paginated-data.sync="paginatedData" />
  </div>
</template>

<script>
import session from '../api/session'
import TagsListWidget from '../components/TagsListWidget.vue'
import cloneDeep from 'lodash/cloneDeep'
import Multiselect from 'vue-multiselect'
import ValuesDisplayMixin from '../mixins/ValuesDisplayMixin'
import Vue from 'vue'
import Paginator from '@/components/Paginator.vue'

const toLower = text => {
  return text.toString().toLowerCase()
}

const searchByNameAndTags = (items, searchTerm, searchTags) => {
  if (searchTerm) {
    items = items.filter(item => {
      let search = `${item.ancestors_name} > ${item.name}`
      return toLower(search).includes(toLower(searchTerm))
    })
  }

  if (searchTags) {
    searchTags.forEach(searchTag => {
      items = items.filter(item => {
        return (
          item.tags.filter(tag => {
            return tag.id === searchTag.id
          }).length > 0
        )
      })
    })
  }

  return items
}

export default {
  name: 'PlacesTable',
  components: {
    TagsListWidget,
    Multiselect,
    Paginator
  },
  mixins: [ValuesDisplayMixin],
  data () {
    return {
      paginatedData: [],
      searchTerm: null,
      searchTags: [],
      searched: [],
      allPlaces: [],
      tagsData: [],
      sortInput: {
        name: 'name',
        type: 'asc'
      },
      message: {
        isDisplayed: false,
        label: '',
        description: ''
      }
    }
  },
  props: {
    placesType: {
      type: String,
      default: undefined
    },
    selectedPlaces: {
      type: Array,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: false
    },
    disabledPlaces: {
      type: Array,
      default: () => []
    },
    fieldsErrors: {
      type: Object,
      default: () => { return {} }
    },
    showCriterionAlert: {
      type: Boolean,
      default: false
    }
  },
  async mounted () {
    this.$store.dispatch('loading/begin_loading')

    let apiUrl = `/api/userdata/places/?is_processed=true`
    if (![undefined, null].includes(this.placesType)) {
      apiUrl = `/api/userdata/places/?type=${this.placesType}&is_processed=true`
    }

    try {
      const placesResponse = await session.get(apiUrl)
      const tagsResponse = await session.get('/api/userdata/tags/')

      this.allPlaces = []

      let selectedPlaces = cloneDeep(this.selectedPlaces)
      let excludeIds = selectedPlaces.map(x => x.id)
      this.allPlaces = placesResponse.data.filter(
        item => !excludeIds.includes(item.id)
      )
      this.allPlaces.push(...selectedPlaces)
      this.allPlaces.sort((a, b) => a.name.localeCompare(b.name))
      this.searched = this.allPlaces
      this.tagsData = tagsResponse.data

      /*
      ugly workaround for a bug in vue-material
      https://github.com/vuematerial/vue-material/issues/1904
      */
      let selectedPlacesBackup = []
      selectedPlacesBackup.push(...selectedPlaces)
      setTimeout(async () => {
        await this.$nextTick()
        this.$emit('update:selectedPlaces', [])
        this.$emit('update:selectedPlaces', selectedPlacesBackup)
      }, 300)
      this.$store.dispatch('loading/end_loading')
    } catch (error) {
      this.$store.dispatch('loading/end_loading')
      this.message.label = 'Erreur'
      this.message.description = error.message
      this.message.isDisplayed = true
    }
  },
  methods: {
    getSelectedCountLabel (count) {
      const single = `${count} lieu sélectionné`
      const plural = `${count} lieux sélectionnés`

      if (count > 1) {
        return plural
      } else {
        return single
      }
    },
    onSelect (items) {
      this.$emit('update:selectedPlaces', items)
    },
    async searchOnTable () {
      await Vue.nextTick()
      this.searched = searchByNameAndTags(
        this.allPlaces,
        this.searchTerm,
        this.searchTags
      )
    },
    emptySelection () {
      this.$emit('update:selectedPlaces', [])
    },
    getEmptyDescription () {
      if (this.searchTerm !== null) {
        return `Il n'y a pas de lieux correspondant à la recherche \u00ab ${
          this.searchTerm
        } \u00bb.`
      } else {
        return 'Aucun lieux disponibles'
      }
    },
    getDisabledPlaces () {
      const selectedPlacesId = this.selectedPlaces.map(place => place.id)
      return this.disabledPlaces.filter(
        placeId => !selectedPlacesId.includes(placeId)
      )
    },
    isDisabled (item) {
      return !this.editable || this.getDisabledPlaces().includes(item.id)
    },
    tagChoiceName (tag) {
      return tag.name
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../stylesheets/variables";

>>> .md-table-alternate-header {
  position: static;
  z-index: 1;
}

.invalid-form-message {
  margin-bottom: 1em;
}

.md-table {
  &.md-invalid >>> {
    .md-toolbar {
      border: 1px solid #ff1744;
    }
    .md-table-content {
      border: 1px solid #ff1744;
    }
  }
}

.multiselect-transparent {
  >>> .multiselect__tags,
  >>> .multiselect__single,
  >>> .multiselect__placeholder,
  >>> .multiselect__input {
    background: transparent;
    color: #717171;
  }
  min-height: 0px;
  min-width: 240px;

  >>> .multiselect__tags {
    min-height: 0px;
  }

  >>> .multiselect__placeholder {
    font-size: 16px;
  }

  >>> .multiselect__strong {
    margin-top: 8px;
  }
}

.table-title {
  width: 15%;
}

.search-fields {
  width: 85%;
}

.criterion-alert {
  padding: 0.5em 1em;
  text-align: center;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/PlacesTable.vue